import React from 'react';

import { Tool } from './Tool';
import { getTypeIcon } from './ToolTypeUtils';
import { stringToToolType } from './ToolTypes';
import { GraphIcon, resolveToolIcon, resolveTypeIcon } from './WorkflowNodes';

import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Beschriftung des Filterfelds — sichtbar als Platzhalter, für Screenreader als aria-label. */
const FILTER_PLACEHOLDER = 'Filtern nach Name und Beschreibung ...';

export interface CustomerToolsPaletteProps {
    tools: Tool[];
    onMouseDown?: (event: React.MouseEvent, tool: Tool) => void;
    onDragStart?: (event: React.DragEvent, tool: Tool) => void;
    loading?: boolean;
    error?: string | null;
    filterValue?: string;
    onFilterChange?: (value: string) => void;
}

const truncateLabel = (text: string, maxLength: number) =>
    text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`;

/**
 * Rich-content shadcn-styled tooltip body. Mirrors the shape of the old
 * Fluent `renderToolTooltip` (provider · name · description) but inherits
 * the dark `bg-primary` / `text-primary-foreground` look from
 * `@/components/ui/tooltip` so it matches the rest of the portal.
 */
const renderToolTooltipContent = (name: string, description?: string, provider?: string) => (
    <div className="max-w-[320px] space-y-1.5">
        {provider && (
            <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">
                {provider}
            </div>
        )}
        <div className="text-sm font-semibold leading-tight">{name}</div>
        {description && description.trim() && (
            <div className="text-xs leading-snug opacity-90">{description}</div>
        )}
    </div>
);

const CustomerToolsPalette = React.memo(function CustomerToolsPalette({ tools, onMouseDown, onDragStart, loading, error, filterValue = '', onFilterChange }: CustomerToolsPaletteProps) {
    if (loading) {
        return (
            <div className="customer-tools-palette">
                <div className="palette-header">
                    <div className="headerM marginBottomS">Agentische Werkzeuge</div>
                    <p>Verfügbare Werkzeuge werden geladen...</p>
                </div>
                {onFilterChange && (
                    <div className="palette-filter">
                        <Input
                            className="h-8 text-xs"
                            aria-label={FILTER_PLACEHOLDER}
                            placeholder={FILTER_PLACEHOLDER}
                            value={filterValue}
                            onChange={(event) => onFilterChange(event.target.value)}
                        />
                    </div>
                )}
                <div className="palette-items">
                    <div className="loading-placeholder">Laden...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="customer-tools-palette">
                <div className="palette-header">
                    <div className="headerM marginBottomS">Agentische Werkzeuge</div>
                </div>
                <div className="palette-items">
                    <Alert variant="destructive" className="error-message m-2">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                </div>
            </div>
        );
    }

    if (!tools || tools.length === 0) {
        return (
            <div className="customer-tools-palette">
                <div className="palette-header">
                    <div className="headerM marginBottomS">Agentische Werkzeuge</div>
                    <p>No tools available</p>
                </div>
                {onFilterChange && (
                    <div className="palette-filter">
                        <Input
                            className="h-8 text-xs"
                            aria-label={FILTER_PLACEHOLDER}
                            placeholder={FILTER_PLACEHOLDER}
                            value={filterValue}
                            onChange={(event) => onFilterChange(event.target.value)}
                        />
                    </div>
                )}
                <div className="palette-items">
                    <div className="empty-placeholder">No tools found</div>
                </div>
            </div>
        );
    }

    return (
        <div className="customer-tools-palette">
            <div className="palette-header">
                <div className="headerM marginBottomS">Agentische Werkzeuge</div>
            </div>
            {onFilterChange && (
                <div className="palette-filter">
                    <Input
                        className="h-8 text-xs"
                        aria-label={FILTER_PLACEHOLDER}
                        placeholder={FILTER_PLACEHOLDER}
                        value={filterValue}
                        onChange={(event) => onFilterChange(event.target.value)}
                    />
                </div>
            )}
            <TooltipProvider delayDuration={200}>
                <div className="palette-items">
                    {tools.map((tool, index) => {
                        // Use tool ID as key, fallback to index if ID is missing
                        // This ensures unique keys even if some tools have undefined IDs
                        const toolId = tool.getId();
                        const stableKey = toolId ? `${toolId}-${index}` : `tool-${index}-${tool.getName()}-${tool.getType()}`;

                        // DIAGNOSTIC: Log if tool has undefined ID
                        if (!toolId) {
                            console.warn('⚠️ [PALETTE] Tool with undefined ID:', {
                                index,
                                name: tool.getName(),
                                type: tool.getType(),
                                description: tool.getDescription()
                            });
                        }

                        return (
                            <Tooltip key={stableKey}>
                                <TooltipTrigger asChild>
                                    <div
                                        className="palette-item customer-tool relative z-[1]"
                                        draggable={!!onDragStart}
                                        onMouseDown={onMouseDown ? (event) => onMouseDown?.(event, tool) : undefined}
                                        onDragStart={onDragStart ? (event) => onDragStart?.(event, tool) : undefined}
                                        data-tool-id={toolId || `undefined-${index}`}
                                        data-tool-index={index}
                                    >
                                        <div className="palette-item-content">
                                            <GraphIcon
                                                icon={resolveToolIcon(tool.getIcon())}
                                                className="palette-icon"
                                                style={tool.getIconColor() ? { color: tool.getIconColor() } : undefined}
                                            />
                                            <div className="palette-label relative z-[2] max-w-full truncate">
                                                {truncateLabel(tool.getDisplayName(), 30)}
                                            </div>
                                        </div>
                                        {/* Type indicator icon */}
                                        <div className="palette-type-indicator">
                                            <GraphIcon
                                                icon={resolveTypeIcon(getTypeIcon(stringToToolType(tool.getType())))}
                                                className="type-icon"
                                            />
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" align="start" sideOffset={8}>
                                    {renderToolTooltipContent(
                                        tool.getDisplayName(),
                                        tool.getDescription(),
                                        tool.getProviderName(),
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
            </TooltipProvider>
        </div>
    );
});

export default CustomerToolsPalette;
